import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaPaperPlane, FaReceipt, FaRupeeSign, FaSearch, FaShieldAlt, FaUsers, FaWallet } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmModal from "../components/ConfirmModal";
import { useWalletKpis, useWalletUsers, formatDateTime } from "../hooks/useWalletAdminData";
import {
  adminAdjustWallet,
  adminBulkWalletCredit,
  createIdempotencyKey,
} from "../services/walletAdminService";
import { canMutateWallet, canViewWalletModule } from "../utils/walletPermissions";
import "./walletAdmin.css";

function money(v) {
  return Number(v || 0).toFixed(2);
}

const ROLE_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "user", label: "User" },
  { value: "artist", label: "Artist" },
  { value: "mentor", label: "Mentor" },
  { value: "admin", label: "Admin" },
];

const BLOCK_OPTIONS = [
  { value: "all", label: "All Wallets" },
  { value: "active", label: "Active" },
  { value: "blocked", label: "Blocked" },
];

const CREDIT_AUDIENCE_OPTIONS = [
  { value: "single", label: "Single User / Artist / Mentor" },
  { value: "user", label: "All Users" },
  { value: "artist", label: "All Artists" },
  { value: "mentor", label: "All Mentors" },
  { value: "artist_mentor", label: "All Artists & Mentors" },
  { value: "all", label: "All Users, Artists & Mentors" },
];

const CREDIT_NOTE_OPTIONS = [
  { value: "manual", label: "Manual Note" },
  { value: "welcome_bonus", label: "Welcome Bonus" },
  { value: "refund", label: "Refund" },
  { value: "reward", label: "Reward Credit" },
  { value: "promo_credit", label: "Promo Credit" },
];

const CREDIT_REASON_LABELS = {
  manual: "Manual credit",
  welcome_bonus: "Welcome bonus",
  refund: "Refund",
  reward: "Reward credit",
  promo_credit: "Promo credit",
};

const OP_CONFIG = {
  add_balance: { label: "Add", color: "wallet-btn-primary", amount: true },
  debit_balance: { label: "Debit", color: "wallet-btn-secondary", amount: true },
  hold_amount: { label: "Hold", color: "wallet-btn-light", amount: true },
  release_hold: { label: "Release", color: "wallet-btn-light", amount: true },
  block: { label: "Block", color: "wallet-pill-danger", amount: false },
  unblock: { label: "Unblock", color: "wallet-pill-success", amount: false },
};

function buildCreditReason(noteType, note) {
  const label = CREDIT_REASON_LABELS[noteType] || "Manual credit";
  const cleanNote = String(note || "").trim();
  if (!cleanNote) return label;
  if (cleanNote.toLowerCase() === label.toLowerCase()) return label;
  return `${label}: ${cleanNote}`;
}

function describeCreditAudience(audience, phone = "") {
  const cleanPhone = String(phone || "").trim();
  switch (audience) {
    case "single":
      return cleanPhone ? `wallet ${cleanPhone}` : "one wallet";
    case "user":
      return "all users";
    case "artist":
      return "all artists";
    case "mentor":
      return "all mentors";
    case "artist_mentor":
      return "all artists and mentors";
    case "all":
      return "all users, artists, and mentors";
    default:
      return "selected wallets";
  }
}

function ActionModal({
  open,
  user,
  opType,
  onClose,
  onSubmit,
}) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  React.useEffect(() => {
    if (open) {
      setAmount("");
      setReason("");
    }
  }, [open]);

  if (!open || !user) return null;
  const cfg = OP_CONFIG[opType] || OP_CONFIG.add_balance;

  return (
    <div className="wallet-modal-overlay">
      <div className="wallet-modal-card">
        <h3 style={{ marginTop: 0 }}>
          {cfg.label} - {user.phone}
        </h3>
        <div style={{ color: "#475569", marginBottom: 10 }}>
          {user.name || "-"} | Balance Rs {money(user.walletBalance)} | Hold Rs {money(user.walletOnHold)}
        </div>
        {cfg.amount ? (
          <input
            className="wallet-input"
            type="number"
            min="0"
            step="0.01"
            placeholder="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        ) : null}
        <textarea
          className="wallet-textarea"
          placeholder="Reason / admin note"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={{ marginTop: 8 }}
        />
        <div className="wallet-modal-actions">
          <button className="wallet-btn wallet-btn-light" onClick={onClose}>
            Cancel
          </button>
          <button
            className={`wallet-btn ${cfg.color}`}
            onClick={() => onSubmit({ amount, reason })}
          >
            Confirm {cfg.label}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WalletList() {
  const navigate = useNavigate();
  const allowed = canViewWalletModule();
  const canMutate = canMutateWallet();
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [blocked, setBlocked] = useState("all");
  const [actionModal, setActionModal] = useState({
    open: false,
    opType: "add_balance",
    user: null,
  });
  const [actionSheetUser, setActionSheetUser] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditForm, setCreditForm] = useState({
    audience: "single",
    operation: "credit",
    phone: "",
    amount: "",
    noteType: "manual",
    note: "",
  });

  const { rows, loading, error } = useWalletUsers({ search, role, blocked });
  const { kpis, loading: kpiLoading, error: kpiError } = useWalletKpis();

  const fallbackTotals = useMemo(() => {
    return rows.reduce((acc, row) => {
      acc.totalLiability += Number(row.walletBalance || 0);
      acc.totalOnHold += Number(row.walletOnHold || 0);
      return acc;
    }, {
      totalLiability: 0,
      totalOnHold: 0,
    });
  }, [rows]);

  const phoneSuggestions = useMemo(() => {
    const seen = new Set();
    return rows.filter((row) => {
      const key = String(row.phone || "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 160);
  }, [rows]);

  const selectedAudienceProfile = useMemo(() => {
    const cleanKey = String(creditForm.phone || "").trim().toLowerCase();
    if (!cleanKey) return null;
    return phoneSuggestions.find((row) => (
      String(row.phone || "").toLowerCase() === cleanKey ||
      String(row.raw?.walletId || "").toLowerCase() === cleanKey
    )) || null;
  }, [creditForm.phone, phoneSuggestions]);

  const visibleAudienceCount = useMemo(() => {
    const audience = creditForm.audience;
    const cleanPhone = String(creditForm.phone || "").trim();

    if (audience === "single") {
      if (!cleanPhone) return 0;
      return rows.filter((row) => (
        String(row.phone || "") === cleanPhone ||
        String(row.raw?.walletId || "").toLowerCase() === cleanPhone.toLowerCase()
      )).length;
    }

    if (audience === "all") {
      return rows.length;
    }

    if (audience === "artist_mentor") {
      return rows.filter((row) => (row.roles || []).some((roleName) => ["artist", "mentor"].includes(roleName))).length;
    }

    if (audience === "user") {
      return rows.filter((row) => !(row.roles || []).some((roleName) => ["artist", "mentor"].includes(roleName))).length;
    }

    return rows.filter((row) => (row.roles || []).includes(audience)).length;
  }, [creditForm.audience, creditForm.phone, rows]);

  const creditReasonPreview = useMemo(() => (
    buildCreditReason(creditForm.noteType, creditForm.note)
  ), [creditForm.note, creditForm.noteType]);

  if (!allowed) {
    return (
      <div className="wallet-card wallet-page">
        <h3 style={{ marginTop: 0 }}>Access Denied</h3>
        <p>Only admin/super_admin can access Manage Wallets.</p>
      </div>
    );
  }

  const runAction = async ({ opType, user, amount, reason }) => {
    try {
      const needsAmount = OP_CONFIG[opType]?.amount;
      const parsedAmount = Number(amount || 0);
      if (needsAmount && (!Number.isFinite(parsedAmount) || parsedAmount <= 0)) {
        toast.error("Amount must be > 0");
        return;
      }

      setLoadingAction(true);
      await adminAdjustWallet({
        phone: user.phone,
        opType,
        amount: needsAmount ? parsedAmount : 0,
        reason: reason || "",
        idempotencyKey: createIdempotencyKey(`wallet_${opType}`),
      });
      toast.success(`Wallet action applied: ${OP_CONFIG[opType]?.label || opType}`);
      setActionModal({ open: false, opType: "add_balance", user: null });
      setConfirm(null);
    } catch (err) {
      toast.error(err?.message || "Wallet action failed");
    } finally {
      setLoadingAction(false);
    }
  };

  const getCreditPayload = () => {
    const audience = String(creditForm.audience || "single");
    const phone = String(creditForm.phone || "").trim();
    const amount = Number(creditForm.amount || 0);
    const operation = String(creditForm.operation || "credit");
    const note = String(creditForm.note || "").trim();
    const noteType = String(creditForm.noteType || "manual");

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Transfer amount must be greater than 0");
      return null;
    }

    if (audience === "single" && !phone) {
      toast.error("Enter a phone number or wallet ID");
      return null;
    }

    if (audience !== "single" && operation !== "credit") {
      toast.error("Bulk deduct is disabled. Use single wallet deduct.");
      return null;
    }

    if (noteType === "manual" && !note) {
      toast.error("Please write a manual note for this transfer");
      return null;
    }

    return {
      audience,
      phone,
      amount,
      operation,
      note,
      noteType,
      reason: buildCreditReason(noteType, note),
      targetMode: audience === "single" ? "single" : "segment",
      targetScope: audience === "single" ? "" : audience,
      targetLabel: describeCreditAudience(audience, phone),
    };
  };

  const submitWalletCredit = async () => {
    const payload = getCreditPayload();
    if (!payload) return;

    try {
      setCreditLoading(true);
      const result = payload.targetMode === "single" && payload.operation === "debit" ?
        await adminAdjustWallet({
          phone: payload.phone,
          opType: "debit_balance",
          amount: payload.amount,
          reason: payload.reason,
          idempotencyKey: createIdempotencyKey("wallet_debit"),
        }) :
        await adminBulkWalletCredit({
          targetMode: payload.targetMode,
          phone: payload.phone,
          targetScope: payload.targetScope,
          amount: payload.amount,
          noteType: payload.noteType,
          note: payload.note,
          idempotencyKey: createIdempotencyKey("wallet_credit"),
        });

      if (payload.operation === "debit") {
        toast.success(`Deducted Rs ${money(payload.amount)} from wallet. ${result.transactionNo || ""}`);
      } else if (result.failureCount > 0) {
        toast.warn(
          `Credited ${result.successCount} wallet(s). ${result.failureCount} wallet(s) failed.`
        );
      } else {
        toast.success(
          `Credited ${result.successCount} wallet(s) with Rs ${money(payload.amount)} each.`
        );
      }

      setCreditForm((prev) => ({
        ...prev,
        phone: "",
        amount: "",
        note: "",
      }));
      setConfirm(null);
    } catch (err) {
      toast.error(err?.message || "Wallet transfer failed");
    } finally {
      setCreditLoading(false);
    }
  };

  const kpiData = {
    totalLiability: kpis.totalLiability || fallbackTotals.totalLiability,
    totalOnHold: kpis.totalOnHold || fallbackTotals.totalOnHold,
    totalPendingSettlements: kpis.totalPendingSettlements || 0,
    todayCreditedAmount: kpis.todayCreditedAmount || 0,
  };

  const creditTargetLabel = describeCreditAudience(creditForm.audience, creditForm.phone);
  const confirmLoading = loadingAction || creditLoading;
  const transferNotePlaceholder = creditForm.noteType === "manual" ?
    "Write custom note like onboarding gift, booking compensation, or special credit" :
    "Optional extra note for the audit trail";

  return (
    <div className="wallet-page">
      <div className="wallet-grid wallet-grid-kpi">
        <div className="wallet-card wallet-kpi-card">
          <div className="wallet-kpi-title">Total Liability</div>
          <div className="wallet-kpi-value">Rs {money(kpiData.totalLiability)}</div>
        </div>
        <div className="wallet-card wallet-kpi-card">
          <div className="wallet-kpi-title">Total On Hold</div>
          <div className="wallet-kpi-value">Rs {money(kpiData.totalOnHold)}</div>
        </div>
        <div className="wallet-card wallet-kpi-card">
          <div className="wallet-kpi-title">Total Pending Settlements</div>
          <div className="wallet-kpi-value">Rs {money(kpiData.totalPendingSettlements)}</div>
        </div>
        <div className="wallet-card wallet-kpi-card">
          <div className="wallet-kpi-title">Today Credited Amount</div>
          <div className="wallet-kpi-value">Rs {money(kpiData.todayCreditedAmount)}</div>
        </div>
      </div>

      {kpiError ? <div className="wallet-inline-note">{kpiError}</div> : null}
      {kpiLoading ? <div className="wallet-inline-note">Refreshing KPI cards...</div> : null}

      <div className="wallet-card wallet-admin-transfer-card">
        {confirm?.scope === "transfer" ? (
          <div className="wallet-card-local-overlay">
            <div className="wallet-card-local-modal">
              <div className="wallet-local-modal-icon">
                {creditForm.operation === "debit" ? <FaRupeeSign /> : <FaPaperPlane />}
              </div>
              <h3>{confirm.title}</h3>
              <p>{confirm.message}</p>
              <div className="wallet-local-modal-actions">
                <button type="button" className="wallet-btn wallet-btn-light" disabled={confirmLoading} onClick={() => setConfirm(null)}>
                  Cancel
                </button>
                <button type="button" className="wallet-btn wallet-btn-primary" disabled={confirmLoading} onClick={() => confirm?.run?.()}>
                  {confirmLoading ? "Please wait..." : confirm.confirmText}
                </button>
              </div>
            </div>
          </div>
        ) : null}
        <div className="wallet-panel-head">
          <div>
            <div className="wallet-transfer-title-row">
              <span className="wallet-transfer-title-icon"><FaWallet /></span>
              <h3 className="wallet-panel-title">Apna Artist Wallet Credit</h3>
            </div>
            <div className="wallet-panel-copy">
              Credit or deduct one wallet by phone/wallet ID, or credit grouped users, artists, and mentors with full ledger audit.
            </div>
          </div>
        </div>

        <div className="wallet-grid-2 wallet-admin-transfer-grid">
          <div className="wallet-form-stack">
            <div className="wallet-transfer-quick-stats">
              <span><FaShieldAlt /> Audited</span>
              <span><FaReceipt /> TRN ledger</span>
              <span><FaUsers /> Role targets</span>
            </div>
            <div className="wallet-filter-grid">
              <select
                className="wallet-select"
                value={creditForm.audience}
                disabled={!canMutate || creditLoading}
                onChange={(e) => setCreditForm((prev) => ({
                  ...prev,
                  audience: e.target.value,
                }))}
              >
                {CREDIT_AUDIENCE_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>

              <select
                className="wallet-select"
                value={creditForm.operation}
                disabled={!canMutate || creditLoading}
                onChange={(e) => setCreditForm((prev) => ({
                  ...prev,
                  operation: e.target.value,
                }))}
              >
                <option value="credit">Credit wallet</option>
                <option value="debit">Deduct wallet</option>
              </select>

              {creditForm.audience === "single" ? (
                <div>
                  <input
                    className="wallet-input"
                    list="wallet-credit-phone-list"
                    placeholder="Phone number or Wallet ID"
                    value={creditForm.phone}
                    disabled={!canMutate || creditLoading}
                    onChange={(e) => setCreditForm((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))}
                  />
                  <datalist id="wallet-credit-phone-list">
                    {phoneSuggestions.map((row) => (
                      <option key={row.phone} value={row.phone}>
                        {row.name || row.role || row.phone} {row.raw?.walletId ? `- ${row.raw.walletId}` : ""}
                      </option>
                    ))}
                    {phoneSuggestions.filter((row) => row.raw?.walletId).map((row) => (
                      <option key={row.raw.walletId} value={row.raw.walletId}>
                        {row.name || row.phone} - {row.phone}
                      </option>
                    ))}
                  </datalist>
                </div>
              ) : (
                <div className="wallet-transfer-hint">
                  Bulk credit runs on the full users collection for the selected audience.
                </div>
              )}

              <input
                className="wallet-input"
                type="number"
                min="0"
                step="0.01"
                placeholder="Amount per wallet"
                value={creditForm.amount}
                disabled={!canMutate || creditLoading}
                onChange={(e) => setCreditForm((prev) => ({
                  ...prev,
                  amount: e.target.value,
                }))}
              />
            </div>

            <select
              className="wallet-select"
              value={creditForm.noteType}
              disabled={!canMutate || creditLoading}
              style={{ marginTop: 10 }}
              onChange={(e) => setCreditForm((prev) => ({
                ...prev,
                noteType: e.target.value,
              }))}
            >
              {CREDIT_NOTE_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>

            <textarea
              className="wallet-textarea"
              placeholder={transferNotePlaceholder}
              value={creditForm.note}
              disabled={!canMutate || creditLoading}
              style={{ marginTop: 10 }}
              onChange={(e) => setCreditForm((prev) => ({
                ...prev,
                note: e.target.value,
              }))}
            />

            <div className="wallet-inline-note wallet-inline-note-tight">
              Recipient message preview: {creditReasonPreview}. Use manual note when you want a custom message like onboarding credit or recovery refund.
            </div>
          </div>

          <div className="wallet-transfer-summary">
            <div className="wallet-inline-chips">
              <span className="wallet-chip chip-light"><FaReceipt /> One audit entry per wallet</span>
              <span className="wallet-chip chip-active"><FaShieldAlt /> Preset or custom note</span>
            </div>

            {creditForm.audience === "single" && creditForm.phone ? (
              <div className="wallet-transfer-profile">
                <div className="wallet-avatar-badge">
                  {String(selectedAudienceProfile?.name || creditForm.phone).charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="wallet-transfer-profile-title">
                    {selectedAudienceProfile?.name || "Selected wallet"}
                  </div>
                  <div className="wallet-transfer-profile-copy">
                    {selectedAudienceProfile?.phone || creditForm.phone}
                    {selectedAudienceProfile?.raw?.walletId ? ` | ${selectedAudienceProfile.raw.walletId}` : ""}
                    {selectedAudienceProfile?.role ? ` | ${selectedAudienceProfile.role}` : ""}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="wallet-transfer-summary-row">
              <strong><FaUsers /> Target</strong>
              <span>{creditTargetLabel}</span>
            </div>
            <div className="wallet-transfer-summary-row">
              <strong><FaRupeeSign /> {creditForm.operation === "debit" ? "Deduct amount" : "Amount per wallet"}</strong>
              <span>Rs {money(creditForm.amount || 0)}</span>
            </div>
            <div className="wallet-transfer-summary-row">
              <strong><FaReceipt /> Credit note</strong>
              <span>{creditReasonPreview}</span>
            </div>

            <div className="wallet-inline-note" style={{ margin: 0 }}>
              Visible table preview: {visibleAudienceCount} matching wallet(s). Bulk sends are not limited to the table filters and target the full users collection.
            </div>
          </div>
        </div>

        <div className="wallet-card-footer">
          <button
            className="wallet-btn wallet-btn-primary"
            disabled={!canMutate || creditLoading}
            onClick={() => {
              const payload = getCreditPayload();
              if (!payload) return;
              setConfirm({
                scope: "transfer",
                title: payload.operation === "debit" ? "Confirm wallet deduction" : "Confirm wallet credit",
                message: `${payload.operation === "debit" ? "Deduct" : "Credit"} Rs ${money(payload.amount)} ${payload.operation === "debit" ? "from" : "to"} ${payload.targetLabel}. Note: ${payload.reason}`,
                confirmText: payload.operation === "debit" ? "Deduct Amount" : "Send Credit",
                run: submitWalletCredit,
              });
            }}
          >
            {creditLoading ? "Processing..." : creditForm.operation === "debit" ? "Deduct Amount" : "Send Credit"}
          </button>
        </div>
      </div>

      <div className="wallet-card wallet-toolbar-card">
        <div className="wallet-panel-head wallet-panel-head-compact">
          <div>
            <h3 className="wallet-panel-title">Wallet Directory</h3>
            <div className="wallet-panel-copy">
              Search by name or phone, filter roles, and jump into the live ledger before taking action.
            </div>
          </div>
          <span className="wallet-chip chip-light">{rows.length} visible wallet(s)</span>
        </div>

        <div className="wallet-filter-grid">
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <FaSearch color="#64748b" />
            <input
              className="wallet-input"
              placeholder="Search by phone or name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="wallet-select" value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLE_OPTIONS.map((x) => (
              <option key={x.value} value={x.value}>{x.label}</option>
            ))}
          </select>
          <select className="wallet-select" value={blocked} onChange={(e) => setBlocked(e.target.value)}>
            {BLOCK_OPTIONS.map((x) => (
              <option key={x.value} value={x.value}>{x.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="wallet-card wallet-table-card">
        <div className="wallet-panel-head wallet-panel-head-compact">
          <div>
            <h3 className="wallet-panel-title">Live Wallet Table</h3>
            <div className="wallet-panel-copy">
              Review balances, hold values, and block status in one table before sending credits or opening a ledger.
            </div>
          </div>
          <span className="wallet-chip chip-light">
            {loading ? "Refreshing..." : `${rows.length} row(s)`}
          </span>
        </div>

        {loading ? <div className="wallet-empty">Loading wallets...</div> : null}
        {!loading && error ? <div className="wallet-empty">{error}</div> : null}
        {!loading && !error && !rows.length ? <div className="wallet-empty">No wallets found.</div> : null}

        {!loading && !error && rows.length ? (
          <div className="wallet-table-wrap">
            <table className="wallet-table wallet-table-wide">
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Wallet Balance</th>
                  <th>On Hold</th>
                  <th>Lifetime Earnings</th>
                  <th>Blocked</th>
                  <th>Updated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.phone}>
                    <td>{u.phone}</td>
                    <td>{u.name || "-"}</td>
                    <td>{u.role || "-"}</td>
                    <td>Rs {money(u.walletBalance)}</td>
                    <td>Rs {money(u.walletOnHold)}</td>
                    <td>Rs {money(u.walletLifetimeEarnings)}</td>
                    <td>
                      <span className={`wallet-chip ${u.walletBlocked ? "chip-blocked" : "chip-active"}`}>
                        {u.walletBlocked ? "Blocked" : "Active"}
                      </span>
                    </td>
                    <td>{formatDateTime(u.walletUpdatedAt)}</td>
                    <td>
                      <div className="wallet-action-menu">
                        <button
                          className="wallet-icon-btn"
                          type="button"
                          title="Wallet actions"
                          onClick={() => setActionSheetUser(u)}
                        >
                          <FaEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <ActionModal
        open={actionModal.open}
        user={actionModal.user}
        opType={actionModal.opType}
        onClose={() => setActionModal({ open: false, opType: "add_balance", user: null })}
        onSubmit={({ amount, reason }) => runAction({
          opType: actionModal.opType,
          user: actionModal.user,
          amount,
          reason,
        })}
      />

      {actionSheetUser ? (
        <div className="wallet-modal-overlay" onClick={() => setActionSheetUser(null)}>
          <div className="wallet-action-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="wallet-action-sheet-head">
              <div>
                <h3>{actionSheetUser.name || "Wallet Actions"}</h3>
                <p>{actionSheetUser.phone} {actionSheetUser.raw?.walletId ? `| ${actionSheetUser.raw.walletId}` : ""}</p>
              </div>
              <button type="button" className="wallet-icon-btn" onClick={() => setActionSheetUser(null)}>×</button>
            </div>
            <div className="wallet-action-sheet-grid">
              <button type="button" onClick={() => openWalletAction(actionSheetUser, "add_balance")}>Add balance</button>
              <button type="button" onClick={() => openWalletAction(actionSheetUser, "debit_balance")}>Debit balance</button>
              <button type="button" onClick={() => openWalletAction(actionSheetUser, "hold_amount")}>Hold amount</button>
              <button type="button" onClick={() => openWalletAction(actionSheetUser, "release_hold")}>Release hold</button>
              <button
                type="button"
                onClick={() => {
                  const opType = actionSheetUser.walletBlocked ? "unblock" : "block";
                  const user = actionSheetUser;
                  setActionSheetUser(null);
                  setConfirm({
                    title: `${user.walletBlocked ? "Unblock" : "Block"} Wallet`,
                    message: `${user.walletBlocked ? "Unblock" : "Block"} wallet ${user.phone}?`,
                    run: () => runAction({ opType, user, amount: 0, reason: "" }),
                  });
                }}
              >
                {actionSheetUser.walletBlocked ? "Unblock wallet" : "Block wallet"}
              </button>
              <button type="button" onClick={() => navigate(`/panel/wallets/${actionSheetUser.phone}`)}>
                <FaWallet /> Open ledger
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={Boolean(confirm) && confirm?.scope !== "transfer"}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText || "Confirm"}
        loading={confirmLoading}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.run?.()}
      />

      <ToastContainer position="top-right" autoClose={2500} />
    </div>
  );

  function openWalletAction(user, opType) {
    setActionSheetUser(null);
    setActionModal({ open: true, opType, user });
  }
}
